/**
 * CLEARO glass thickness calculator — <clearo-size-calculator>
 *
 * thickness = frame − inside gap − outside gap
 * Shows the result with a count-up, moves a pin along the size scale,
 * highlights matching models and recommends the best fit (the model whose
 * range centre is closest), with price + link from the section's JSON.
 * Markup & styles: sections/clearo-find-size.liquid.
 */
(() => {
  if (customElements.get('clearo-size-calculator')) return;

  const SCALE_MAX = 40; // mm at the right end of the meter
  const COUNT_MS = 550;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const round = (n) => Math.round(n * 10) / 10;
  const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class SizeCalculator extends HTMLElement {
    connectedCallback() {
      const read = (sel) => {
        try {
          return JSON.parse(this.querySelector(sel).textContent);
        } catch (e) {
          return null;
        }
      };
      this.models = (read('[data-models]') || []).filter((m) => Number(m.max) > Number(m.min));
      this.t = read('[data-strings]') || {};
      this.contactUrl = this.dataset.contactUrl || '/pages/contact';
      this.inputs = Array.from(this.querySelectorAll('input[type="number"]'));
      this.valueEl = this.querySelector('[data-value]');
      this.pin = this.querySelector('[data-pin]');
      this.bands = Array.from(this.querySelectorAll('.clr-calc__band'));
      this.rec = this.querySelector('[data-rec]');
      this.shown = null;
      this.lastKey = '';

      this.inputs.forEach((input) => input.addEventListener('input', () => this.update()));
      const example = this.querySelector('[data-example]');
      if (example) example.addEventListener('click', () => this.fillExample());
    }

    fillExample() {
      const values = ['30', '4', '3'];
      if (reducedMotion.matches) {
        this.inputs.forEach((input, i) => (input.value = values[i]));
        this.update();
        return;
      }
      // Type the numbers in one after another for a bit of life
      this.inputs.forEach((input) => (input.value = ''));
      this.update();
      values.forEach((v, i) =>
        setTimeout(() => {
          this.inputs[i].value = v;
          this.update();
        }, 180 + i * 260)
      );
    }

    update() {
      const [a, b, c] = this.inputs.map((i) => (i.value === '' ? null : Number(i.value)));

      if (a === null || b === null || c === null || [a, b, c].some((n) => Number.isNaN(n))) {
        this.setValue(null);
        this.render('empty');
        return;
      }

      const thickness = round(a - b - c);
      if (a <= 0 || b < 0 || c < 0 || thickness <= 0) {
        this.setValue(null);
        this.render('invalid');
        return;
      }

      this.setValue(thickness);

      const fits = this.models
        .filter((m) => thickness >= m.min && thickness <= m.max)
        .sort((x, y) => Math.abs(thickness - (x.min + x.max) / 2) - Math.abs(thickness - (y.min + y.max) / 2));

      if (fits.length) return this.render('fit', fits);

      const min = Math.min(...this.models.map((m) => m.min));
      const max = Math.max(...this.models.map((m) => m.max));
      if (thickness > min && thickness < max) {
        // Between sizes: recommend the next size up
        const next = this.models.filter((m) => m.min > thickness).sort((x, y) => x.min - y.min)[0];
        if (next) {
          const band = this.bands[this.models.indexOf(next)];
          if (band) band.classList.add('is-match');
          return this.render('fit', [next], this.t.gapPick);
        }
      }
      this.render(thickness < min ? 'tooThin' : thickness > max ? 'tooThick' : 'gap');
    }

    setValue(value) {
      // Pin on the meter
      if (value === null) {
        this.pin.classList.remove('is-on');
      } else {
        this.pin.style.setProperty('--pin', `${Math.min(100, (value / SCALE_MAX) * 100)}%`);
        this.pin.classList.add('is-on');
      }

      this.bands.forEach((band, i) => {
        const m = this.models[i];
        band.classList.toggle('is-match', !!m && value !== null && value >= m.min && value <= m.max);
      });

      // Count-up number
      cancelAnimationFrame(this.countFrame);
      if (value === null) {
        this.valueEl.textContent = '–';
        this.shown = null;
        return;
      }
      const from = this.shown ?? 0;
      if (reducedMotion.matches || from === value) {
        this.valueEl.textContent = fmt(value);
        this.shown = value;
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / COUNT_MS);
        const eased = 1 - Math.pow(1 - p, 3);
        this.valueEl.textContent = fmt(round(from + (value - from) * eased));
        if (p < 1) this.countFrame = requestAnimationFrame(step);
      };
      this.shown = value;
      this.countFrame = requestAnimationFrame(step);
    }

    render(state, fits = [], note = '') {
      const key = state + ':' + fits.map((f) => f.label).join('|') + ':' + note;
      if (key === this.lastKey) return; // avoid re-announcing / re-animating the same result
      this.lastKey = key;

      const t = this.t;
      if (state === 'fit') {
        const [best, ...others] = fits;
        const also = others.length
          ? `<p class="clr-calc__also">${esc(t.alsoFits)} ${others
              .map((o) => `<a href="${esc(o.url)}">${esc(o.label)}</a>`)
              .join(', ')}</p>`
          : '';
        this.rec.innerHTML = `
          <div class="clr-calc__model">
            <div>
              <p class="clr-calc__model-kicker">${esc(t.best)}</p>
              <p class="clr-calc__model-name">${esc(best.label)}</p>
              <p class="clr-calc__model-meta">${esc(best.title)}${best.price ? ' · ' + esc(best.price) : ''}</p>
            </div>
            <a class="clr-btn clr-btn--primary" href="${esc(best.url)}">${esc(t.button)}
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M4 10h11m0 0-4.5-4.5M15 10l-4.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </a>
          </div>${note ? `<p class="clr-calc__also">${esc(note)}</p>` : ''}${also}`;
        return;
      }

      const contact = ['gap', 'tooThin', 'tooThick'].includes(state)
        ? ` <a href="${esc(this.contactUrl)}">${esc(t.contact)}</a>`
        : '';
      this.rec.innerHTML = `<p class="clr-calc__msg">${esc(t[state] || '')}${contact}</p>`;
    }
  }

  customElements.define('clearo-size-calculator', SizeCalculator);
})();
